import {useMemo, useCallback, useContext} from 'react';
import {useDispatch} from 'react-redux';
import moment from 'moment-timezone';
import {type List} from 'immutable';
import i18next from 'i18next';

import * as Famlytics from 'web-app/util/famlytics/index';
import {TrackingEvents} from 'web-app/util/famlytics/events';
import ChildPlanBehaviorsEntity from 'web-app/react/entities/plans/child-plans-behaviors-entity';
import BehaviorsCollection from 'web-app/behaviors/behaviors-collection';
import {type Plan} from 'web-app/react/entities/plans/model';
import * as ManagePlanHelpers from 'web-app/react/plans/manage-plan/helpers';
import * as ManagePlanActions from 'web-app/react/plans/manage-plan/actions';
import * as PlansSelectors from 'web-app/react/entities/plans/selectors';
import useTypedSelector from 'web-app/react/hooks/use-typed-selector';
import {hasValue} from '@famly/stat_ts-utils_has-value';
import {isFailed} from 'web-app/util/failable';
import {navigationContext} from 'web-app/react/contexts/navigation';
import {useCountry} from 'web-app/react/staffing-attendance/hooks/queries/use-country';
import type {PricingGroupFragment} from 'web-app/react/bookings-finance/settings/pricing-groups/__generated__/pricing-group.fragment.api-types';
import {useScheduleBreakModal} from 'web-app/react/schedule-break/hooks/use-schedule-break-modal';
import {transformPlans as transformModelPlansToScheduleBreakPlans} from 'web-app/react/schedule-break/helpers/transform-iplan-to-plan';
import * as GlobalEvent from 'web-app/services/global-event';
import * as SentryService from 'web-app/services/sentry';

import * as PlanActions from './actions';
import * as Selectors from './selectors';
import * as Constants from './constants';
import * as Actions from './actions';
import {useManageUSPlans} from './use-manage-us-plans';

const emptyBehaviors = new BehaviorsCollection([]);

const usePlansFunctions = (config: {
    institutionId: string;
    childId: string;
    childLocalStartDate: string;
    pricingGroups: PricingGroupFragment[];
    canUseAutomaticPricingGroup: boolean;
    plans: List<Plan>;
    refetchFinanceOverviewChildren?: () => Promise<void>;
    shouldCreateUsPlan?: boolean | undefined;
}) => {
    const {
        institutionId,
        childId,
        childLocalStartDate,
        pricingGroups,
        canUseAutomaticPricingGroup,
        plans,
        shouldCreateUsPlan: shouldCreateUsPlanNullable,
    } = config;
    const shouldCreateUsPlan = shouldCreateUsPlanNullable ?? false;
    const {transitionToPath} = useContext(navigationContext);

    const dispatch = useDispatch();

    const childPlanBehaviors = useTypedSelector(state => {
        const behaviors = ChildPlanBehaviorsEntity.selectors.getById(state, {id: institutionId});
        return behaviors ? behaviors.behaviors : emptyBehaviors;
    });

    /**
     * Create the onCreatePlan function
     */

    const onCreatePlan = useCallback(() => {
        const pricingGroupId = pricingGroups
            ? ManagePlanHelpers.getAutoPricingGroupId(canUseAutomaticPricingGroup, pricingGroups)
            : null;

        dispatch(
            ManagePlanActions.showCreatePlan.action({
                institutionId,
                childPlanBehaviors,
                childId,
                validTo: undefined,
                validOnMustBeAfter: childLocalStartDate,
                validToMustBeAfter: childLocalStartDate,
                trackingContext: TrackingEvents.CREATE_PLAN,
                pricingGroupId,
            }),
        );
    }, [
        institutionId,
        childId,
        childLocalStartDate,
        childPlanBehaviors,
        pricingGroups,
        canUseAutomaticPricingGroup,
        dispatch,
    ]);

    /**
     * Create the onEditPlan function
     */
    const onEditPlan = useMemo(() => {
        return createOnEditPlan(
            {
                institutionId,
                childId,
                childLocalStartDate,
                plans,
            },
            dispatch,
        );
    }, [institutionId, childId, childLocalStartDate, plans, dispatch]);

    /**
     * Create the onSchedulePlan function
     */
    const latestPlan = useMemo(() => plans.first(), [plans]);

    const firstDayAfterLatestScheduledFrom = useTypedSelector(state =>
        Selectors.firstDayAfterLatestScheduledFrom(state, {
            childId,
            referenceId: childId,
        }),
    );

    const sessionsUsedByLastestPlan = useTypedSelector(state => {
        if (latestPlan && latestPlan.id) {
            return PlansSelectors.getSessionsById(state, {id: latestPlan.id});
        }

        return null;
    });

    const productsUsedByLastestPlan = useTypedSelector(state => {
        if (latestPlan && latestPlan.id) {
            return PlansSelectors.getProductsById(state, {id: latestPlan.id});
        }

        return null;
    });

    const manageUsPlans = useManageUSPlans(institutionId, childId);

    const onSchedulePlan = useCallback(() => {
        if (!latestPlan) {
            return;
        }

        if (shouldCreateUsPlan) {
            if (!manageUsPlans.simplifiedUSBillingPlanWithPlanTemplatesModal.isTemplatesLoading) {
                manageUsPlans.simplifiedUSBillingPlanWithPlanTemplatesModal.open('single');
            }
        } else {
            dispatch(
                ManagePlanActions.showCreatePlan.action({
                    institutionId,
                    behaviors: childPlanBehaviors,
                    childId,
                    pricingGroupId: latestPlan.pricingGroupId,
                    editPlan: latestPlan,
                    planUsedSessions: sessionsUsedByLastestPlan,
                    planUsedProducts: productsUsedByLastestPlan,
                    validOnMustBeAfter: firstDayAfterLatestScheduledFrom,
                    validToMustBeAfter: firstDayAfterLatestScheduledFrom,
                    onAfterSave: () => {
                        if (hasValue(config.refetchFinanceOverviewChildren)) {
                            config.refetchFinanceOverviewChildren();
                        }
                    },
                }),
            );
        }
    }, [
        institutionId,
        childId,
        sessionsUsedByLastestPlan,
        productsUsedByLastestPlan,
        childPlanBehaviors,
        firstDayAfterLatestScheduledFrom,
        latestPlan,
        dispatch,
        config,
        manageUsPlans.simplifiedUSBillingPlanWithPlanTemplatesModal,
        shouldCreateUsPlan,
    ]);

    const handleErrorMessage = (msg: string) => {
        GlobalEvent.updateError(
            i18next.t('react.profile.childProfile.plansAndInvoices.translations.json.somethingWentWrong'),
        );
        SentryService.captureMessage(msg);
    };

    const scheduleBreakModal = useScheduleBreakModal({
        institutionSetId: institutionId,
        afterSubmission: () => {
            dispatch(PlanActions.fetchChildPlansData.action(institutionId, childId));
            if (hasValue(config.refetchFinanceOverviewChildren)) {
                config.refetchFinanceOverviewChildren();
            }
        },
    });
    const scheduleBreakAvailable = scheduleBreakModal.isFeatureAvailable && !scheduleBreakModal.isLoading;
    const onScheduleBreak = () => {
        Famlytics.track(TrackingEvents.CHILD_PROFILE_SCHEDULE_BREAK_BUTTON_CLICKED);
        if (scheduleBreakAvailable) {
            const inputPlans = transformModelPlansToScheduleBreakPlans(plans);
            if (isFailed(inputPlans)) {
                handleErrorMessage(`Failed to convert plans for schedule a break: ${inputPlans.error}`);
                return;
            }
            const child = {
                childId,
                plans: inputPlans.value,
            };
            scheduleBreakModal.open({children: [child]});
        }
    };

    /**
     * Create the getOnInsertAfter function
     */
    const onInsertAfterFunction = useMemo(() => {
        return createOnInsertAfter({institutionId, childId}, dispatch);
    }, [institutionId, childId, dispatch]);

    /**
     * Invariant: there can never be "holes" between plans. A plan must
     * start the next day of the previous plan
     *
     * It's possible to insert a plan after if:
     * - the plan is _not_ the latest plan (checked elsewhere)
     * - the plan is _not_ open ended
     * - the plan is more than one day long
     */
    const getOnInsertAfter = useCallback(
        (plan: Plan) => {
            if (plan.to && plan.from) {
                const fromMoment = plan.from;
                const toMoment = plan.to.clone().subtract(1, 'day');
                if (fromMoment.isBefore(toMoment, 'day')) {
                    return (sessions, products) => onInsertAfterFunction(plan, sessions, products);
                }
            }

            return undefined;
        },
        [onInsertAfterFunction],
    );

    /**
     * Create the getOnInsertNewEarliestPlan function
     */
    const firstDayANewFirstPlanCanBeValidOn = useMemo(
        () => moment(childLocalStartDate).format(Constants.momentDayFormat),
        [childLocalStartDate],
    );

    const onInsertNewEarliestPlanFunction = useMemo(() => {
        return createOnInsertNewEarliestPlan(
            {
                institutionId,
                childId,
                pricingGroups,
                canUseAutomaticPricingGroup,
                childPlanBehaviors,
                firstDayANewFirstPlanCanBeValidOn,
            },
            dispatch,
        );
    }, [
        institutionId,
        childId,
        pricingGroups,
        canUseAutomaticPricingGroup,
        childPlanBehaviors,
        firstDayANewFirstPlanCanBeValidOn,
        dispatch,
    ]);

    /**
     * It's possible to insert a plan before if:
     * - the plan is the earliest plan (checked elsewhere)
     * - the earliest plan starts _after_ the child's registration date
     */
    const getOnInsertNewEarliestPlan = useCallback(
        (earliestPlan: Plan) => {
            if (earliestPlan.from && earliestPlan.from.isAfter(firstDayANewFirstPlanCanBeValidOn, 'day')) {
                return () => onInsertNewEarliestPlanFunction(earliestPlan);
            }

            return undefined;
        },
        [firstDayANewFirstPlanCanBeValidOn, onInsertNewEarliestPlanFunction],
    );

    const isSettingChildDateOfBirth: boolean = useTypedSelector(
        state => state.childProfile.childPlans.submittingDateOfBirth,
    );

    const onSetChildDateOfBirth = useCallback(
        (date: Date) => {
            dispatch(Actions.addDateOfBirth.action(childId, moment(date).format('YYYY-MM-DD')));
        },
        [childId, dispatch],
    );

    const navigateToInvoicingBasics = useCallback(() => {
        transitionToPath('/account/institution/:institutionId/financeOnboarding', {
            institutionId,
        });
    }, [institutionId, transitionToPath]);

    const country = useCountry(institutionId);

    const howPlanWorksHelpArticle = useMemo(() => {
        if ((['UK', 'US', 'Canada'] as string[]).includes(country)) {
            return 'https://help.famly.co/en/articles/4912287-creating-a-plan-for-a-child';
        }

        if ((['Austria', 'Germany', 'Switzerland'] as string[]).includes(country)) {
            return 'https://help.famly.co/de/collections/2969332-rund-um-aktuelle-betreuungsplane';
        }

        return null;
    }, [country]);

    return {
        onSchedulePlan,
        isSchedulePlanLoading: shouldCreateUsPlan && manageUsPlans.simplifiedUSBillingPlanModal.isLoading,
        onCreatePlan,
        onEditPlan,
        onSetChildDateOfBirth,
        isSettingChildDateOfBirth,
        navigateToInvoicingBasics,
        howPlanWorksHelpArticle,
        getOnInsertAfter,
        getOnInsertNewEarliestPlan,
        scheduleBreakAvailable,
        onScheduleBreak,
    };
};

export const getOnEditPlanOptions = (plan: Plan, plans: List<Plan>, childLocalStartDate: string) => {
    const startOfDay = plan?.from;

    if (!startOfDay) {
        // We should never get here. A plan always has a from date.
        return null;
    }

    const plansBefore = plans.filter(p => p.to && p.to.isSameOrBefore(startOfDay));
    const plansAfter = plans.filter(p => p.from && p.from.isAfter(startOfDay));

    const {validOnMustBeBefore, validToMustBeBefore} = (() => {
        const lastPlanAfter = plansAfter.last();

        if (!lastPlanAfter || !lastPlanAfter?.from) {
            return {
                validOnMustBeBefore: undefined,
                validToMustBeBefore: undefined,
            };
        }

        const nextPlanFrom = lastPlanAfter.from;

        const theDayTheNextPlanStarts = nextPlanFrom;
        const theDayBeforePlanStarts = theDayTheNextPlanStarts.clone().subtract(1, 'day');

        return {
            validOnMustBeBefore: theDayBeforePlanStarts.format(Constants.momentDayFormat),
            validToMustBeBefore: theDayTheNextPlanStarts.format(Constants.momentDayFormat),
        };
    })();

    const {validOnMustBeAfter, validToMustBeAfter} = (() => {
        const firstPlanBefore = plansBefore.first();

        if (!firstPlanBefore) {
            const childStartDate = moment(childLocalStartDate);
            const theDayAfterChildStartDate = childStartDate.clone().add(1, 'day');

            return {
                validOnMustBeAfter: childStartDate.format(Constants.momentDayFormat),
                validToMustBeAfter: theDayAfterChildStartDate.format(Constants.momentDayFormat),
            };
        } else {
            const theDayThePreviousPlanStarts = firstPlanBefore.from;
            const oneDayAfterThePreviousPlanStarts = theDayThePreviousPlanStarts!.clone().add(1, 'day');
            const twoDaysAfterThePreviousPlanStarts = oneDayAfterThePreviousPlanStarts.clone().add(1, 'day');

            return {
                validOnMustBeAfter: oneDayAfterThePreviousPlanStarts.format(Constants.momentDayFormat),
                validToMustBeAfter: twoDaysAfterThePreviousPlanStarts.format(Constants.momentDayFormat),
            };
        }
    })();

    return {
        validOnMustBeBefore,
        validToMustBeBefore,
        validOnMustBeAfter,
        validToMustBeAfter,
        isLastPlan: plansAfter.size === 0,
    };
};

/*
|------------------------------------------------------------------------------
| Helper functions
|------------------------------------------------------------------------------
*/
const createOnEditPlan = (
    config: {
        institutionId: string;
        childId: string;
        childLocalStartDate: string;
        plans: List<Plan>;
    },
    dispatch: ReturnType<typeof useDispatch>,
): ((plan: Plan, planUsedSessions: List<any>, planUsedProducts: List<any>) => void) => {
    const {institutionId, childId, childLocalStartDate, plans} = config;

    return (plan, planUsedSessions, planUsedProducts) => {
        const editPlanOptions = getOnEditPlanOptions(plan, plans, childLocalStartDate);

        if (!hasValue(editPlanOptions)) {
            return;
        }

        const {isLastPlan, validOnMustBeAfter, validOnMustBeBefore, validToMustBeAfter, validToMustBeBefore} =
            editPlanOptions;

        dispatch(
            ManagePlanActions.showEditPlan.action({
                institutionId,
                childId,
                editPlan: plan,
                planUsedSessions,
                planUsedProducts,
                canEditValidOn: true,
                canEditValidTo: isLastPlan,
                validOnMustBeAfter,
                validOnMustBeBefore,
                validToMustBeAfter, // Not allowed by backend yet, so the validTo field is disabled
                validToMustBeBefore, // Not allowed by backend yet, so the validTo field is disabled
            }),
        );
    };
};

const createOnInsertAfter = (
    config: {
        institutionId: string;
        childId: string;
    },
    dispatch: ReturnType<typeof useDispatch>,
) => {
    return (plan: Plan, planUsedSessions, planUsedProducts) => {
        const fromMoment = plan.from;
        const toMoment = plan.to;

        if (!fromMoment || !toMoment) {
            return;
        }

        const {institutionId, childId} = config;

        const validOnMustBeAfter = fromMoment.clone().add(1, 'day');
        const validOnMustBeBefore = toMoment.clone().subtract(1, 'day').format(Constants.momentDayFormat);

        dispatch(
            ManagePlanActions.insertPlan.action({
                institutionId,
                childId,
                editPlan: plan.merge({
                    from: validOnMustBeAfter,
                }),
                planUsedSessions,
                planUsedProducts,
                canEditValidOn: true,
                validOnMustBeAfter: validOnMustBeAfter.format(Constants.momentDayFormat),
                validOnMustBeBefore,
            }),
        );
    };
};

const createOnInsertNewEarliestPlan = (
    config: {
        institutionId: string;
        childId: string;
        pricingGroups: PricingGroupFragment[];
        canUseAutomaticPricingGroup: boolean;
        childPlanBehaviors: BehaviorsCollection;
        firstDayANewFirstPlanCanBeValidOn: string;
    },
    dispatch,
) => {
    const {
        institutionId,
        childId,
        pricingGroups,
        canUseAutomaticPricingGroup,
        childPlanBehaviors,
        firstDayANewFirstPlanCanBeValidOn,
    } = config;

    return (earliestPlan: Plan) => {
        const pricingGroupId = pricingGroups
            ? ManagePlanHelpers.getAutoPricingGroupId(canUseAutomaticPricingGroup, pricingGroups)
            : null;

        // The new earliest plan must be constrained to start on or after
        // the child's registration date and end on the day before
        // the current earliest plan starts.
        const earliestPlanStartDate = firstDayANewFirstPlanCanBeValidOn;
        const latestPlanStartDate =
            earliestPlan.from?.clone().subtract(1, 'day').format(Constants.momentDayFormat) ?? undefined;

        const validToMustBeOn =
            earliestPlan.from?.clone().subtract(1, 'day').format(Constants.momentDayFormat) ?? undefined;

        dispatch(
            ManagePlanActions.showCreatePlan.action({
                institutionId,
                behaviors: childPlanBehaviors,
                childId,
                validOn: firstDayANewFirstPlanCanBeValidOn,
                validTo: validToMustBeOn,
                validOnMustBeAfter: earliestPlanStartDate,
                validOnMustBeBefore: latestPlanStartDate,
                // If the earliest plan starts the day after the the child was registered,
                // the only valid day to create a new first plan on is the day the child
                // was registered
                canEditValidOn: latestPlanStartDate !== firstDayANewFirstPlanCanBeValidOn,
                canEditValidTo: false,
                trackingContext: TrackingEvents.CREATE_PLAN,
                pricingGroupId,
            }),
        );
    };
};

export default usePlansFunctions;
